import type { SyncedCustomPayment } from '@/types/api';

interface CustomPaymentFormProps {
  formData: Partial<SyncedCustomPayment>;
  onChange: (data: Partial<SyncedCustomPayment>) => void;
}

export function CustomPaymentForm({ formData, onChange }: CustomPaymentFormProps) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
        <input type="text" value={formData.name || ''} onChange={(e) => onChange({ ...formData, name: e.target.value })} className="input w-full" required />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center">
          <input type="checkbox" id="cp-isRequiredRefNumber" checked={formData.isRequiredRefNumber ?? false} onChange={(e) => onChange({ ...formData, isRequiredRefNumber: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
          <label htmlFor="cp-isRequiredRefNumber" className="ml-2 text-sm text-gray-700">Req. Ref Number</label>
        </div>
        <div className="flex items-center">
          <input type="checkbox" id="cp-isUseForPayByVoucher" checked={formData.isUseForPayByVoucher ?? false} onChange={(e) => onChange({ ...formData, isUseForPayByVoucher: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
          <label htmlFor="cp-isUseForPayByVoucher" className="ml-2 text-sm text-gray-700">Pay By Voucher</label>
        </div>
        <div className="flex items-center">
          <input type="checkbox" id="cp-isUniqueReferenceNumber" checked={formData.isUniqueReferenceNumber ?? false} onChange={(e) => onChange({ ...formData, isUniqueReferenceNumber: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
          <label htmlFor="cp-isUniqueReferenceNumber" className="ml-2 text-sm text-gray-700">Unique Ref</label>
        </div>
        <div className="flex items-center">
          <input type="checkbox" id="cp-isUseFrontButton" checked={formData.isUseFrontButton ?? false} onChange={(e) => onChange({ ...formData, isUseFrontButton: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
          <label htmlFor="cp-isUseFrontButton" className="ml-2 text-sm text-gray-700">Front Button</label>
        </div>
        <div className="flex items-center">
          <input type="checkbox" id="cp-isOpenDrawerOnPayment" checked={formData.isOpenDrawerOnPayment ?? false} onChange={(e) => onChange({ ...formData, isOpenDrawerOnPayment: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
          <label htmlFor="cp-isOpenDrawerOnPayment" className="ml-2 text-sm text-gray-700">Open Drawer</label>
        </div>
        <div className="flex items-center">
          <input type="checkbox" id="cp-isVoucher" checked={formData.isVoucher ?? false} onChange={(e) => onChange({ ...formData, isVoucher: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
          <label htmlFor="cp-isVoucher" className="ml-2 text-sm text-gray-700">Voucher</label>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Ref Field Name</label>
        <input type="text" value={formData.refNumberFieldName || ''} onChange={(e) => onChange({ ...formData, refNumberFieldName: e.target.value })} className="input w-full" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Card Type</label>
        <input type="text" value={formData.cardType || ''} onChange={(e) => onChange({ ...formData, cardType: e.target.value })} className="input w-full" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
        <input type="number" value={formData.sortOrder ?? ''} onChange={(e) => onChange({ ...formData, sortOrder: parseInt(e.target.value) || 0 })} className="input w-full" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Button Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={formData.buttonColorCode != null ? '#' + (formData.buttonColorCode >>> 0).toString(16).padStart(6, '0').slice(-6) : '#ffffff'}
              onChange={(e) => onChange({ ...formData, buttonColorCode: parseInt(e.target.value.slice(1), 16) })}
              className="h-9 w-14 cursor-pointer rounded border border-gray-300 p-0.5"
            />
            <input
              type="text"
              value={formData.buttonColorCode != null ? '#' + (formData.buttonColorCode >>> 0).toString(16).padStart(6, '0').slice(-6) : ''}
              onChange={(e) => {
                const hex = e.target.value.replace('#', '');
                if (/^[0-9a-fA-F]{0,6}$/.test(hex)) {
                  onChange({ ...formData, buttonColorCode: hex.length === 6 ? parseInt(hex, 16) : undefined });
                }
              }}
              placeholder="#ffffff"
              className="input w-full"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={formData.textColorCode != null ? '#' + (formData.textColorCode >>> 0).toString(16).padStart(6, '0').slice(-6) : '#000000'}
              onChange={(e) => onChange({ ...formData, textColorCode: parseInt(e.target.value.slice(1), 16) })}
              className="h-9 w-14 cursor-pointer rounded border border-gray-300 p-0.5"
            />
            <input
              type="text"
              value={formData.textColorCode != null ? '#' + (formData.textColorCode >>> 0).toString(16).padStart(6, '0').slice(-6) : ''}
              onChange={(e) => {
                const hex = e.target.value.replace('#', '');
                if (/^[0-9a-fA-F]{0,6}$/.test(hex)) {
                  onChange({ ...formData, textColorCode: hex.length === 6 ? parseInt(hex, 16) : undefined });
                }
              }}
              placeholder="#000000"
              className="input w-full"
            />
          </div>
        </div>
      </div>
      {formData.buttonColorCode != null && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Preview</label>
          <div
            className="rounded px-4 py-2 text-center font-medium"
            style={{
              backgroundColor: '#' + ((formData.buttonColorCode ?? 0xffffff) >>> 0).toString(16).padStart(6, '0').slice(-6),
              color: '#' + ((formData.textColorCode ?? 0) >>> 0).toString(16).padStart(6, '0').slice(-6),
            }}
          >
            {formData.name || 'Custom Payment'}
          </div>
        </div>
      )}
    </div>
  );
}
